import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, Building2, Users, Award, MapPin } from 'lucide-react';

export const Partners = () => {
  const mainPartners = [
    {
      name: 'Société CORUM',
      president: 'Laurent RECOUVREUR',
      photo: 'https://customer-assets.emergentagent.com/job_business-web-creator-3/artifacts/nftcnoq4_IMG_2420.jpeg',
      subtitle: 'Une histoire familiale',
      description: 'Fondé par Claude Recouvreur et dirigé par Laurent Recouvreur, Corum a été créé pour répondre à la demande croissante de logements au Grand-Duché de Luxembourg. Principalement actif dans l\'immobilier résidentiel, le groupe s\'est élargi au développement des surfaces commerciales, bureaux ainsi que dans le secteur de l\'hôtellerie. Toujours soucieux de créer de nouvelles opportunités, le Groupe développe également des jardins d\'enfants, des maisons de retraite et de soins infirmiers.',
      regions: ['Luxembourg', 'Espagne', 'France'],
      achievements: [
        'Plus de 50 projets livrés',
        '205 lots réalisés',
        'Résidence Romina - 10 appartements',
        'Résidence Oradour - 6 logements',
        'Résidence Lorenzo - 12 logements',
      ],
      expertise: 'Promotion immobilière internationale',
    },
    {
      name: 'Société KAPA',
      president: 'Thierry VALERI',
      photo: 'https://customer-assets.emergentagent.com/job_business-web-creator-3/artifacts/8vsj96k4_IMG_2421.jpeg',
      subtitle: 'Expertise et savoir-faire',
      description: 'Fort de plus de 20 ans d\'expérience dans la maîtrise d\'œuvre et la conduite de projets, Thierry Valeri a exercé en tant que directeur de programmes et maître d\'œuvre libéral pendant 14 ans, pilotant des opérations variées dans les secteurs résidentiel, commercial et tertiaire. Auparavant, il a occupé pendant 7 ans le poste de chef de projets au sein de SAS Les Arches à Jouy-aux-Arches, où il a développé une expertise pointue. Grâce à sa vision globale du métier, son autonomie et sa capacité à mobiliser les équipes autour d\'objectifs partagés, il a mené avec succès de nombreux projets exigeants, en veillant à conjuguer qualité, sécurité et performance.',
      regions: ['France'],
      achievements: [
        'Fondation de KAPA le 1er décembre 2019',
        'Programme 33 logements locatifs sociaux',
        'Programme 36 logements locatifs sociaux',
        'Programme 6 logements avec parking souterrain',
      ],
      expertise: 'Promoteur-constructeur spécialisé',
    },
  ];

  const clientPartners = [
    { 
      name: 'Immobilière 3F', 
      logo: 'https://customer-assets.emergentagent.com/job_business-web-creator-3/artifacts/w5z4p8ir_IMG_2419.jpeg',
      type: 'Groupe ActionLogement' 
    },
    { 
      name: 'Vivest', 
      logo: 'https://customer-assets.emergentagent.com/job_business-web-creator-3/artifacts/emdqj39p_IMG_2418.jpeg',
      type: 'Groupe ActionLogement' 
    },
    { 
      name: 'Maisons & Cités', 
      logo: 'https://customer-assets.emergentagent.com/job_business-web-creator-3/artifacts/1i7afec3_IMG_2417.jpeg',
      type: 'Bailleur social' 
    },
  ];

  const partnershipBenefits = [
    {
      icon: Building2,
      title: 'Expertise complémentaire',
      description: 'Nos partenaires apportent des compétences spécifiques qui renforcent notre offre globale.',
    },
    {
      icon: Users,
      title: 'Réseau étendu',
      description: 'Un accès privilégié aux bailleurs sociaux et institutionnels majeurs du secteur.',
    },
    {
      icon: Award,
      title: 'Qualité garantie',
      description: 'Des partenaires sélectionnés pour leur excellence et leur engagement qualité.',
    },
  ];

  return (
    <div data-testid="partners-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Nos partenaires</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0A0A0A] uppercase tracking-tighter leading-tight mb-6">
              Un réseau<br />
              <span className="text-[#F25F19]">d'excellence</span>
            </h1>
            <p className="text-lg md:text-xl text-[#71717A] max-w-xl">
              Nous collaborons avec des partenaires de confiance pour vous offrir 
              les meilleures solutions immobilières.
            </p>
          </div>
        </div>
      </section>

      {/* Main Partners */}
      <section data-testid="main-partners" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Partenaires stratégiques</p>
            <h2 className="section-title">Nos alliés clés</h2>
          </div>

          <div className="space-y-16">
            {mainPartners.map((partner, index) => (
              <div
                key={index}
                className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start bg-[#F4F4F5] rounded-sm overflow-hidden"
              >
                {/* Photo du président */}
                {partner.photo && (
                  <div className="lg:col-span-1">
                    <img 
                      src={partner.photo} 
                      alt={partner.president}
                      className="w-full h-full object-cover min-h-[300px]"
                    />
                  </div>
                )}
                
                {/* Informations */}
                <div className={`p-8 lg:p-10 ${partner.photo ? 'lg:col-span-1' : 'lg:col-span-2'}`}>
                  <p className="text-[#F25F19] font-bold uppercase tracking-widest text-sm mb-2">
                    {partner.subtitle || 'Partenaire stratégique'}
                  </p>
                  <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tighter mb-2">
                    {partner.name}
                  </h3>
                  <p className="text-[#71717A] font-medium mb-4">
                    Président : {partner.president}
                  </p>
                  <p className="text-[#71717A] mb-6 text-sm leading-relaxed">
                    {partner.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {partner.regions.map((region, rIndex) => (
                      <span
                        key={rIndex}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-white rounded-sm text-sm"
                      >
                        <MapPin className="w-3 h-3 text-[#F25F19]" />
                        {region}
                      </span>
                    ))}
                  </div>
                  <div className="inline-block bg-[#F25F19] text-white text-sm font-medium px-4 py-2 rounded-sm">
                    {partner.expertise}
                  </div>
                </div>
                
                {/* Réalisations */}
                <div className="bg-[#0A0A0A] p-8 lg:p-10 h-full lg:col-span-1">
                  <h4 className="text-lg font-bold text-white uppercase tracking-wide mb-6">
                    Réalisations
                  </h4>
                  <ul className="space-y-3">
                    {partner.achievements.map((achievement, aIndex) => (
                      <li key={aIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#F25F19] mt-0.5 flex-shrink-0" />
                        <span className="text-white/80 text-sm">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Partners - Bailleurs Sociaux */}
      <section data-testid="client-partners" className="py-20 lg:py-32 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Nos clients</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A0A0A] tracking-tighter">
              Ils nous font confiance
            </h2>
            <p className="text-[#71717A] mt-4 max-w-2xl mx-auto">
              Les bailleurs sociaux qui acquièrent l'ensemble de nos programmes immobiliers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {clientPartners.map((client, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-sm text-center hover:shadow-lg transition-all duration-300 border border-[#E4E4E7]"
              >
                <div className="h-24 flex items-center justify-center mb-4">
                  <img 
                    src={client.logo} 
                    alt={client.name}
                    className="max-h-20 w-auto object-contain"
                  />
                </div>
                <h4 className="text-[#0A0A0A] font-bold mb-1">{client.name}</h4>
                <p className="text-[#71717A] text-sm">
                  Acheteur de l'ensemble immobilier
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section data-testid="benefits-section" className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Avantages</p>
            <h2 className="section-title">Pourquoi nos partenariats</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {partnershipBenefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-sm text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-[#F25F19] rounded-sm flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold tracking-tight mb-4">{benefit.title}</h3>
                <p className="text-[#71717A]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Become Partner CTA */}
      <section className="py-20 lg:py-24 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0A0A0A] rounded-sm p-8 lg:p-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[#F25F19] font-bold uppercase tracking-widest mb-4">Rejoignez-nous</p>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tighter mb-6">
                  Devenez partenaire
                </h2>
                <p className="text-white/70 mb-8">
                  Vous êtes promoteur, constructeur ou bailleur social ? Rejoignez notre 
                  réseau de partenaires et bénéficiez de notre expertise pour développer 
                  vos projets immobiliers.
                </p>
                <Link to="/contact">
                  <Button
                    data-testid="partner-contact-btn"
                    className="bg-[#F25F19] hover:bg-[#CC480B] text-white font-bold uppercase tracking-wide px-8 py-4 rounded-sm"
                  >
                    Nous contacter
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#18181B] p-6 rounded-sm text-center">
                  <p className="text-[#F25F19] font-bold mono text-3xl">10+</p>
                  <p className="text-white/60 text-sm mt-2">Partenaires actifs</p>
                </div>
                <div className="bg-[#18181B] p-6 rounded-sm text-center">
                  <p className="text-[#F25F19] font-bold mono text-3xl">150+</p>
                  <p className="text-white/60 text-sm mt-2">Logements livrés</p>
                </div>
                <div className="bg-[#18181B] p-6 rounded-sm text-center">
                  <p className="text-[#F25F19] font-bold mono text-3xl">20+</p>
                  <p className="text-white/60 text-sm mt-2">Ans d'expérience</p>
                </div>
                <div className="bg-[#18181B] p-6 rounded-sm text-center">
                  <p className="text-[#F25F19] font-bold mono text-3xl">100%</p>
                  <p className="text-white/60 text-sm mt-2">Satisfaction</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter mb-6">
            Construisons ensemble
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto mb-10">
            Rejoignez notre réseau de partenaires de confiance et participez 
            à la construction de l'habitat de demain.
          </p>
          <Link to="/contact">
            <Button
              className="bg-[#F25F19] hover:bg-[#CC480B] text-white font-bold uppercase tracking-wide px-8 py-6 text-base rounded-sm"
            >
              Contactez-nous
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Partners;
